class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.heading = this.querySelector('.cart-notification__heading');
    this.defaultHeading = this.heading?.textContent.trim() || '';
    this.defaultCartLinkText = document.getElementById('cart-notification-button')?.textContent.trim() || 'View cart';
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');
    trapFocus(this.notification);
    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    if (!this.notification.classList.contains('active')) return;
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);
    if (typeof trapFocusHandlers === 'undefined' || !trapFocusHandlers.container ||
        this.notification.contains(trapFocusHandlers.container)) {
      removeTrapFocus(this.activeElement);
    }
  }

  disconnectedCallback() {
    this.close();
    document.body.removeEventListener('click', this.onBodyClick);
  }

  renderContents(parsedState, options = {}) {
    // Adds can return one line or an items array; changes return a full cart.
    // Full carts must use the transaction's target, never an unrelated first item.
    const items = Array.isArray(parsedState.items) ? parsedState.items : parsedState.key ? [parsedState] : [];
    let itemKey = options.itemKey || parsedState.key;
    let item = itemKey ? items.find((entry) => entry.key === itemKey) : undefined;
    if (options.variantId) {
      const matches = items.filter(
        (item) =>
          Number(item.variant_id || item.id) === Number(options.variantId) &&
          !item.parent_relationship?.parent &&
          !item.parent_relationship?.parent_key
      );
      const exactMatch = matches.find((item) => item.key === itemKey);
      // Cart discounts can change a line key. Only use a unique variant match.
      item = exactMatch || (matches.length === 1 ? matches[0] : undefined);
      itemKey = item?.key;
    } else if (!itemKey && !Object.hasOwn(parsedState, 'item_count') && items.length === 1) {
      item = items[0];
      itemKey = item.key;
    }
    if (item?.parent_relationship?.parent || item?.parent_relationship?.parent_key) {
      item = undefined;
      itemKey = undefined;
    }
    this.cartItemKey = item ? itemKey : undefined;

    const heading = options.message || this.defaultHeading;
    if (this.heading) this.heading.textContent = heading;
    if (heading) this.notification.setAttribute('aria-label', heading);
    const successIcon = this.querySelector('.cart-notification__success-icon');
    if (successIcon) {
      successIcon.hidden = options.success === false;
      successIcon.style.display = options.success === false ? 'none' : '';
    }

    // An omitted section must not leave a previous product or shipping claim visible.
    this.renderProduct(parsedState, item, options);
    document.getElementById('cart-notification-shipping')?.replaceChildren();
    const cartLink = document.getElementById('cart-notification-button');
    if (cartLink) cartLink.textContent = this.defaultCartLinkText;

    this.getSectionsToRender().forEach((section) => {
      if (section.id === 'cart-notification-product') return;
      const target = document.getElementById(section.id);
      const sectionHTML = parsedState.sections?.[section.id];

      // Optional cart UI sections must never turn a successful add request
      // into a customer-facing cart error. Shopify can occasionally omit a
      // section response in preview/editor sessions or on a slow connection.
      if (!target || typeof sectionHTML !== 'string') return;
      const innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
      if (innerHTML !== null) target.innerHTML = innerHTML;
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  renderProduct(parsedState, item, options) {
    const target = document.getElementById('cart-notification-product');
    if (!target) return;
    target.replaceChildren();
    const html = parsedState.sections?.['cart-notification-product'];
    if (typeof html === 'string' && this.cartItemKey) {
      const source = new DOMParser().parseFromString(html, 'text/html');
      let card = source.getElementById(`cart-notification-product-${this.cartItemKey}`);
      const marker = item?.properties?._lashnova_upgrade_id;
      if (!card && marker) {
        const matches = [...source.querySelectorAll('[data-ln-cart-variant-id]')].filter((entry) =>
          Number(entry.dataset.lnCartVariantId) === Number(item.variant_id || item.id) &&
          entry.dataset.lnCartUpgradeId === String(marker) && entry.dataset.lnCartNested !== 'true'
        );
        if (matches.length === 1) card = matches[0];
      }
      if (card?.innerHTML.trim()) {
        target.innerHTML = card.innerHTML;
        return;
      }
    }
    if (item && this.renderItemSummary(target, item, parsedState)) return;
    const message = document.createElement('p');
    message.className = 'cart-notification-product__message';
    message.textContent = options.success === false
      ? options.fallbackMessage || this.dataset.fallbackMessage || 'View your cart to confirm the updated items.'
      : this.dataset.fallbackMessage || 'View your cart to confirm the updated items.';
    target.appendChild(message);
  }

  renderItemSummary(target, item, parsedState) {
    const title = item.product_title || item.title;
    if (typeof title !== 'string' || !title.trim()) return false;
    const image = item.featured_image?.url || item.image;
    if (typeof image === 'string' && image) {
      const media = document.createElement('div');
      media.className = 'cart-notification-product__image global-media-settings';
      const img = document.createElement('img');
      img.src = image;
      img.alt = item.featured_image?.alt || title;
      img.width = 70;
      img.height = 70;
      img.loading = 'lazy';
      media.appendChild(img);
      target.appendChild(media);
    }
    const details = document.createElement('div');
    details.className = 'cart-notification-product__details';
    const name = document.createElement('h3');
    name.className = 'cart-notification-product__name h4';
    name.textContent = title;
    details.appendChild(name);
    const meta = document.createElement('div');
    meta.className = 'cart-notification-product__meta';
    const currency = (typeof parsedState.currency === 'string' ? parsedState.currency : parsedState.currency?.iso_code) || this.dataset.currency;
    if (item.final_price !== undefined && item.final_price !== null && Number.isFinite(Number(item.final_price)) && currency) {
      try {
        const price = document.createElement('span');
        price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency', currency,
        }).format(Number(item.final_price) / 100);
        if (this.dataset.currencyCodeEnabled === 'true') price.textContent += ` ${currency}`;
        meta.appendChild(price);
      } catch (_error) {}
    }
    if (Number.isInteger(Number(item.quantity)) && Number(item.quantity) > 1) {
      const quantity = document.createElement('span');
      quantity.textContent = `${this.dataset.quantityLabel || 'Quantity'} ${item.quantity}`;
      meta.appendChild(quantity);
    }
    if (meta.childElementCount) details.appendChild(meta);
    const options = document.createElement('dl');
    const appendOption = (label, value) => {
      const row = document.createElement('div');
      row.className = 'product-option';
      const term = document.createElement('dt');
      term.textContent = `${label}:`;
      const description = document.createElement('dd');
      description.textContent = String(value);
      row.append(term, description);
      options.appendChild(row);
    };
    (item.options_with_values || []).forEach((option) => {
      if (option.value && option.value !== 'Default Title') appendOption(option.name, option.value);
    });
    Object.entries(item.properties || {}).forEach(([label, value]) => {
      if (!label.startsWith('_') && value !== null && value !== '') appendOption(label, value);
    });
    if (options.childElementCount) details.appendChild(options);
    const planName = item.selling_plan_allocation?.selling_plan?.name;
    if (planName) {
      const plan = document.createElement('p');
      plan.className = 'product-option';
      plan.textContent = planName;
      details.appendChild(plan);
    }
    target.appendChild(details);
    return true;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-notification-shipping',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const section = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return section ? section.innerHTML : null;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
